import React from "react";
import { User, Hash, Calendar, CheckSquare, Type } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { FormikProps } from "formik";
import { getIn } from "formik";
import type { SilaeFormData } from "@/types/silae";
import { useTranslation } from "react-i18next";

// Types de variables questionnaire salarié selon le guide SILAE
export interface QuestionnaireVariable {
  type: "double" | "int" | "boolean" | "date" | "text";
  index: number; // 1 à 10
  label?: string; // Libellé personnalisé de la question
  description?: string; // Description de la variable
}

// Indices disponibles (1 à 10)
const AVAILABLE_INDICES = Array.from({ length: 10 }, (_, i) => i + 1);

export interface QuestionnaireVariableSelectProps {
  formik: FormikProps<SilaeFormData>;
  sourceFieldPrefix: string; // Ex: "ifBranch.bonuses.0.bases.0"
  label?: string;
  description?: string;
  showPercentage?: boolean; // Afficher le champ pourcentage pour les types numériques (défaut: true)
  onlyNumericTypes?: boolean; // Afficher uniquement les types numériques (double, int) pour les sources de base/taux
  className?: string;
}

/**
 * QuestionnaireVariableSelect
 *
 * Composant simplifié pour la sélection et configuration d'une variable questionnaire salarié.
 * Basé sur le guide SILAE : Sal.double[X], Sal.Int[X], Sal.bool[X], Sal.date[X], Sal.text[X]
 * où X peut être entre 1 et 10.
 *
 * Nouvelle structure simplifiée :
 * - formik: Pour accéder et mettre à jour les valeurs
 * - sourceFieldPrefix: Chemin complet vers la source (ex: "ifBranch.bonuses.0.bases.0")
 *
 * Tous les champs sont accédés via getIn(formik.values, `${sourceFieldPrefix}.fieldName`)
 */
export const QuestionnaireVariableSelect: React.FC<
  QuestionnaireVariableSelectProps
> = ({
  formik,
  sourceFieldPrefix,
  label,
  description,
  showPercentage = true,
  onlyNumericTypes = false,
  className,
}) => {
  const { t } = useTranslation();

  // Configuration des types de variables questionnaire
  const QUESTIONNAIRE_TYPES = [
    {
      type: "double" as const,
      label: t("questionnaireVariableSelect.variableType.types.double.label"),
      code: "sal.double",
      description: t(
        "questionnaireVariableSelect.variableType.types.double.description",
      ),
      icon: <Hash className="h-4 w-4" />,
      placeholder: t(
        "questionnaireVariableSelect.variableType.types.double.placeholder",
      ),
      inputType: "number",
      step: "0.01",
    },
    {
      type: "int" as const,
      label: t("questionnaireVariableSelect.variableType.types.int.label"),
      code: "sal.int",
      description: t(
        "questionnaireVariableSelect.variableType.types.int.description",
      ),
      icon: <Hash className="h-4 w-4" />,
      placeholder: t(
        "questionnaireVariableSelect.variableType.types.int.placeholder",
      ),
      inputType: "number",
      step: "1",
    },
    {
      type: "boolean" as const,
      label: t("questionnaireVariableSelect.variableType.types.bool.label"),
      code: "sal.bool",
      description: t(
        "questionnaireVariableSelect.variableType.types.bool.description",
      ),
      icon: <CheckSquare className="h-4 w-4" />,
      placeholder: t(
        "questionnaireVariableSelect.variableType.types.bool.placeholder",
      ),
      inputType: "select",
      options: [
        {
          value: "true",
          label: t(
            "questionnaireVariableSelect.variableType.types.bool.options.true",
          ),
        },
        {
          value: "false",
          label: t(
            "questionnaireVariableSelect.variableType.types.bool.options.false",
          ),
        },
      ],
    },
    {
      type: "date" as const,
      label: t("questionnaireVariableSelect.variableType.types.date.label"),
      code: "sal.date",
      description: t(
        "questionnaireVariableSelect.variableType.types.date.description",
      ),
      icon: <Calendar className="h-4 w-4" />,
      placeholder: t(
        "questionnaireVariableSelect.variableType.types.date.placeholder",
      ),
      inputType: "text",
    },
    {
      type: "text" as const,
      label: t("questionnaireVariableSelect.variableType.types.text.label"),
      code: "sal.text",
      description: t(
        "questionnaireVariableSelect.variableType.types.text.description",
      ),
      icon: <Type className="h-4 w-4" />,
      placeholder: t(
        "questionnaireVariableSelect.variableType.types.text.placeholder",
      ),
      inputType: "text",
    },
  ];

  // Utiliser les valeurs par défaut traduites si non fournies
  const finalLabel = label || t("questionnaireVariableSelect.defaultLabel");
  const finalDescription =
    description || t("questionnaireVariableSelect.defaultDescription");

  // Récupération des valeurs depuis Formik
  const questionnaireType = getIn(
    formik.values,
    `${sourceFieldPrefix}.questionnaireVariableType`,
  ) as string | undefined;

  const questionnaireIndex = getIn(
    formik.values,
    `${sourceFieldPrefix}.questionnaireVariableIndex`,
  ) as string | undefined;

  const questionnaireLabel = getIn(
    formik.values,
    `${sourceFieldPrefix}.questionnaireVariableLabel`,
  ) as string | undefined;

  const questionnairePercentage = getIn(
    formik.values,
    `${sourceFieldPrefix}.questionnaireVariablePercentage`,
  ) as string | undefined;

  // Récupération des erreurs et statut touched
  const typeError = getIn(
    formik.errors,
    `${sourceFieldPrefix}.questionnaireVariableType`,
  );
  const indexError = getIn(
    formik.errors,
    `${sourceFieldPrefix}.questionnaireVariableIndex`,
  );
  const percentageError = getIn(
    formik.errors,
    `${sourceFieldPrefix}.questionnaireVariablePercentage`,
  );

  const typeTouched = getIn(
    formik.touched,
    `${sourceFieldPrefix}.questionnaireVariableType`,
  );
  const indexTouched = getIn(
    formik.touched,
    `${sourceFieldPrefix}.questionnaireVariableIndex`,
  );
  const percentageTouched = getIn(
    formik.touched,
    `${sourceFieldPrefix}.questionnaireVariablePercentage`,
  );

  // Filtrer les types selon onlyNumericTypes
  const availableTypes = onlyNumericTypes
    ? QUESTIONNAIRE_TYPES.filter((t) => t.type === "double" || t.type === "int")
    : QUESTIONNAIRE_TYPES;

  // Gestion du changement de type
  const handleTypeChange = (newType: string) => {
    formik.setFieldValue(
      `${sourceFieldPrefix}.questionnaireVariableType`,
      newType,
    );
    // Réinitialiser la valeur lors du changement de type
    formik.setFieldValue(`${sourceFieldPrefix}.questionnaireVariableValue`, "");
    // Initialiser le pourcentage pour les types numériques
    if (newType === "double" || newType === "int") {
      formik.setFieldValue(
        `${sourceFieldPrefix}.questionnaireVariablePercentage`,
        "100",
      );
    }
  };

  // Gestion du changement d'indice
  const handleIndexChange = (newIndex: string) => {
    formik.setFieldValue(
      `${sourceFieldPrefix}.questionnaireVariableIndex`,
      newIndex,
    );
  };

  // Rendu du champ pourcentage pour les types numériques
  const renderPercentageInput = () => {
    if (
      !showPercentage ||
      (questionnaireType !== "double" && questionnaireType !== "int")
    ) {
      return null;
    }

    const hasError = percentageTouched && percentageError;

    return (
      <div className="space-y-2">
        <Label className="text-sm">
          {t("questionnaireVariableSelect.percentage.label")}
        </Label>
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <Input
              type="number"
              min="0.01"
              max="100"
              step="0.01"
              value={questionnairePercentage || "100"}
              onChange={(e) => {
                formik.setFieldValue(
                  `${sourceFieldPrefix}.questionnaireVariablePercentage`,
                  e.target.value,
                );
              }}
              onBlur={() =>
                formik.setFieldTouched(
                  `${sourceFieldPrefix}.questionnaireVariablePercentage`,
                  true,
                )
              }
              placeholder={t(
                "questionnaireVariableSelect.percentage.placeholder",
              )}
              className={cn("pr-8", hasError ? "border-red-500" : "")}
            />
            <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              %
            </span>
          </div>
          <span className="text-xs text-muted-foreground">/ 100</span>
        </div>
        {hasError && (
          <p className="text-xs text-red-500">{String(percentageError)}</p>
        )}
        <p className="text-xs text-muted-foreground">
          {t("questionnaireVariableSelect.percentage.helpText")}
        </p>
      </div>
    );
  };

  return (
    <div
      className={cn(
        "space-y-4 border rounded-lg p-4 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20",
        className,
      )}
    >
      {/* En-tête */}
      <div className="flex items-center gap-2">
        <User className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
        <div>
          <span className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
            {finalLabel}
          </span>
          <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-0.5">
            {finalDescription}
          </p>
        </div>
      </div>

      {/* Sélection du type de variable */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">
          {t("questionnaireVariableSelect.variableType.label")}
        </Label>
        <Select
          value={questionnaireType || ""}
          onValueChange={handleTypeChange}
        >
          <SelectTrigger
            className={cn(typeTouched && typeError ? "border-red-500" : "")}
          >
            <SelectValue
              placeholder={t(
                "questionnaireVariableSelect.variableType.placeholder",
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {availableTypes.map((type) => (
              <SelectItem key={type.type} value={type.type}>
                <div className="flex items-center gap-2">
                  {type.icon}
                  <div className="flex flex-col items-start">
                    <div className="font-medium">{type.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {type.code}[X] - {type.description}
                    </div>
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {typeTouched && typeError && (
          <p className="text-xs text-red-500">{String(typeError)}</p>
        )}
      </div>

      {/* Sélection de l'indice (1-10) */}
      {questionnaireType && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {t("questionnaireVariableSelect.index.label")}
          </Label>
          <Select
            value={questionnaireIndex || ""}
            onValueChange={handleIndexChange}
          >
            <SelectTrigger
              className={cn(indexTouched && indexError ? "border-red-500" : "")}
            >
              <SelectValue
                placeholder={t("questionnaireVariableSelect.index.placeholder")}
              />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_INDICES.map((index) => (
                <SelectItem key={index} value={index.toString()}>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{index}</Badge>
                    <span>
                      {t("questionnaireVariableSelect.index.questionNumber", {
                        index,
                      })}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {indexTouched && indexError && (
            <p className="text-xs text-red-500">{String(indexError)}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {t("questionnaireVariableSelect.index.helpText")}
          </p>
        </div>
      )}

      {/* Libellé personnalisé de la question */}
      {questionnaireType && questionnaireIndex && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            {t("questionnaireVariableSelect.label.label")}
          </Label>
          <Input
            value={questionnaireLabel || ""}
            onChange={(e) => {
              formik.setFieldValue(
                `${sourceFieldPrefix}.questionnaireVariableLabel`,
                e.target.value,
              );
            }}
            placeholder={t("questionnaireVariableSelect.label.placeholder")}
          />
          <p className="text-xs text-muted-foreground">
            {t("questionnaireVariableSelect.label.helpText")}
          </p>
        </div>
      )}

      {/* Champ pourcentage pour les types numériques */}
      {renderPercentageInput()}


      {/* Informations d'aide */}
      <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
          <p>
            <strong>{t("questionnaireVariableSelect.help.title")}</strong>{" "}
            {t("questionnaireVariableSelect.help.description")}
          </p>
          <ul className="list-disc list-inside ml-2 space-y-0.5">
            <li>{t("questionnaireVariableSelect.help.steps.0")}</li>
            <li>{t("questionnaireVariableSelect.help.steps.1")}</li>
            <li>{t("questionnaireVariableSelect.help.steps.2")}</li>
            <li>{t("questionnaireVariableSelect.help.steps.3")}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
